import React, { useEffect, useState } from "react";
import { Datepicker, MbscCalendarEvent } from "@mobiscroll/react";
import Form from "react-bootstrap/Form";
import Toaster, { ToastConfig } from "../../components/Toaster";
import { TaskCategory, TaskStatus, TaskType } from "../../interfaces/task";
import { Loader } from "../../components/Loader";
import AsyncTypeahead, { DataListType } from "../../components/AsyncTypeahead";
import { createTaskApi } from "../../api-helpers/TaskApiHelper";
import { createCommonUtilsApi, createWorkflowApi } from "../../api-helpers/CommonUtilsApiHelper";
import { createUsersApi } from "../../api-helpers/UserApiHelper";
import { createPatientApi } from "../../api-helpers/PatientApiHelper";
import Lookups, { Constants } from "../../constants/Lookups";
import { Lookup } from "common-util-experience-client";
import { PatientPersonalInfo } from "patients-experience-client";
import { OrderItemsDto, SaveTask, TaskDetailDto, TaskPriority, TaskTypes, UpdateTask } from "tasks-experience-client";
import Spinner from "../../components/Spinner";
import '../../assets/scss/custom/_custom.scss';
import { CommonDropdown } from "../../components/DropDown/CommonDropdown";
import { LookupDropdown } from "../../components/DropDown/LookupDropdown";
import { formatDateTask } from "../../helper/dateFormatter";
import { useAppointmentActivityLogger } from "../../hooks/useAppointmentActivityLogger";
import {
  AppointmentActivityAction,
  AppointmentActivityType,
} from "../../constants/AppointmentActivity";

type AddUpdateTaskProps = {
  patient?: PatientPersonalInfo;
  onSubmitTask: (toasterConfig: ToastConfig) => void;
  setOffCanvasShow: (data: any) => void;
  selectedTask?: TaskDetailDto;
  isUpdate: boolean;
  appointmentId?:string;
  taskId?: string;
  reloadGrid?: () => void,
  enableDueDate?:boolean
};

const AddUpdateTask: React.FC<AddUpdateTaskProps> = ({
  patient,
  onSubmitTask,
  setOffCanvasShow,
  selectedTask,
  isUpdate,
  appointmentId,
  taskId,
  reloadGrid,
  enableDueDate=false
}) => {
  const [validated, setValidated] = useState(false);
  const [categories, setCategories] = useState<TaskCategory[]>([]);
  const [tasks, setTasks] = useState<TaskType[]>([]);
  const [priority, setPriority] = useState<TaskPriority[]>([]);
  const [statusList, setStatusList] = useState<TaskStatus[]>([]);
  const logAppointmentActivity = useAppointmentActivityLogger();
  const [loading, setLoading] = useState<boolean>(true);
  const [patientsList, setPatientsList] = useState<DataListType[]>([]);
  const [coachList, setCoachList] = useState<DataListType[]>([]);
  const [validPatient, setValidPatient] = useState<boolean>(true);
  const [validCoach, setValidCoach] = useState<boolean>(true);
  const [addTaskForm, setAddTaskForm] = useState<SaveTask>({} as SaveTask);
  const [openPicker, setOpenPicker] = useState<boolean>(false);
  const [taskTypes, setTaskTypes] = useState<TaskTypes[]>([])
  const [taskTypeOrder, setTaskTypeOrder] = useState<boolean>(false)
  const [orderItems, setOrderItems] = useState<OrderItemsDto[]>([])
  const [orderItemsCheckbox, setOrderItemsCheckbox] = useState<string[]>([])
  const [forceProirityChange,setForcePriorityChange] = useState<string>()
  const [requireAssignee, setRequireAssignee] = useState(true)

  const [updateTaskForm, setUpdateTaskForm] = useState<UpdateTask>({
    assignedUserId: selectedTask?.assignedUserId as string,
    dueAt: selectedTask?.dueAt as Date,
    priorityId: selectedTask?.priorityId as string,
    comment: '',
    taskStatus: selectedTask?.state.stateId,
    statusReason: '',
    wfStateId: selectedTask?.state.stateId
  });

  const [error, setError] = useState({
    dueAt: false,
  });

  const patientApi = createPatientApi()
  const taskApi = createTaskApi()
  const lookup = createCommonUtilsApi()
  const coach = createUsersApi()

  const formChangeHandlar = (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = event.target;
    if(isUpdate) {
      setUpdateTaskForm({
        ...updateTaskForm,
        [name]: value,
      });
    } else {
      setAddTaskForm({
        ...addTaskForm,
        [name]: value,
      });
    }
    if (event.target.name === "taskTypeId") {
      getTaskType(value);
    }
    if (event.target.name === "taskSubtypeId" && taskTypeOrder){
      getOrderItems(value)
    }
    if(!taskTypeOrder){
      setOrderItems([])
    }
  };

  const getOrderItems = async(taskSubtypeId:string)=> {
    setLoading(true);
    try{
      const response = await taskApi.taskControllerGetOrderItemsByTaskSubtypeId(taskSubtypeId);
      const required_products = new Set(response.data.filter(orderItem=>orderItem.isRequired==true).map(item => item.id))
      setOrderItemsCheckbox([...Array.from(required_products)])
      setOrderItems(response.data)
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  }

  const onCategoryUpdate = async(category: string) => {
    if(!isUpdate){
      setAddTaskForm({
        ...addTaskForm,
        taskTypeId: category
      });
      const orderTaskType = taskTypes.filter(tt=>tt.name === Constants.TASK_TYPE_SUPPLY_ORDER)[0]?.id
      if(orderTaskType === category){
        setForcePriorityChange(priority.filter(prio=>prio.type===Constants.PRIORITY_LOW)[0].id)
        setAddTaskForm({...addTaskForm, dueAt:new Date(), priorityId:priority.filter(prio=>prio.type===Constants.PRIORITY_LOW)[0].id, taskTypeId: category})
        setTaskTypeOrder(true)
        setRequireAssignee(false)
      }else{
        setTaskTypeOrder(false)
        setRequireAssignee(true)
        setForcePriorityChange("")
      }
      getTaskType(category);
    }
  }

  const onPriorityUpdate = (priority: string) => {
    if(isUpdate) {
      setUpdateTaskForm({
        ...updateTaskForm,
        priorityId: priority
      });
    } else {
      setAddTaskForm({
        ...addTaskForm,
        priorityId: priority
      });
    }
  }

  const updateFormChangeHandlar = (event: any) => {
    const { name, value } = event.target;
    setUpdateTaskForm({
      ...updateTaskForm,
      [name]: value,
      wfStateId: value
    });
  };

  const dateRangeChangeHandlar = (name: string, event: MbscCalendarEvent) => {
    const selectedDate = new Date(event.value);
    const utcDate = new Date(
      Date.UTC(
        selectedDate.getFullYear(),
        selectedDate.getMonth(),
        selectedDate.getDate()
      )
    );
    if(isUpdate) {
      setUpdateTaskForm({
        ...updateTaskForm,
        [name]: utcDate,
      });
    } else {
      setAddTaskForm({
        ...addTaskForm,
        [name]: utcDate,
      });
    }
    if (event.value) {
      setError({
        ...error,
        [name]: false
      });
    }
  };

  useEffect(() => {
    if (error.dueAt) {
      const label = document.querySelector('label[for="dueAt"]');
      if (label) {
        const input = label.nextElementSibling?.querySelector('input.mbsc-textfield');
        if (input) {
          input.classList.add('invalid-input');
        }
      }
    }
  }, [error]);

  const handleDiscard = () => {
    setOffCanvasShow(false);
  };
  const selectPatient = (selected: any) => {
    if (!selected?.key)
      return;
    setAddTaskForm({
      ...addTaskForm,
      patientId: selected && selected.key,
    });
  };

  const unsetPatientId = () => {
    setAddTaskForm({
      ...addTaskForm,
      patientId: '',
    });
  }

  const openInNewTab = (url: string) => {
    const newWindow = window.open(url, '_blank', 'noopener,noreferrer');
    if (newWindow) newWindow.opener = null;
  }

  const selectCoach = (selected: any) => {
    if(isUpdate) {
      setUpdateTaskForm({
        ...updateTaskForm,
        assignedUserId: selected && selected.key,
      });
    } else {
      setAddTaskForm({
        ...addTaskForm,
        assignedUserId: selected && selected.key,
      });
    }
  };

  const loadPatients = async (query: string) => {
    if (!query) {
      setPatientsList([]);
      return;
    }
    setLoading(true);
    try {
      const response = await patientApi.patientsControllerSearchPatient(query);
      const results = response.data.map((result: any) => ({
        key: result.key, // Use id as the key
        value: result.value, // Display name as the value
        // dob: result.infoDate
      }));
      setPatientsList(results);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const loadCoachesList = async (query: string) => {
    if (!query) {
      setCoachList([]);
      return;
    }
    setLoading(true);
    try {
      const response = await coach.usersControllerSearchUsers(undefined, query)
      const results = response.data.map((result: any) => ({
        key: result.key, // Use id as the key
        value: result.value, // Display name as the value
        // dob: result.infoDate
      }));
      setCoachList(results);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };
  const submitHandler = async (event: any) => {
    event.preventDefault();
    setValidated(true);
    setError({
      ...error,
      dueAt: isUpdate ? !updateTaskForm.dueAt : !addTaskForm.dueAt,
    });

    if(!updateTaskForm.dueAt && !addTaskForm.dueAt) {
      return;
    }

    const form = event.currentTarget;
    if (!form.checkValidity()) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    await setValidPatient(true);
    await setValidCoach(true);
    if(isUpdate) {
      if (!updateTaskForm.assignedUserId) {
        !updateTaskForm.assignedUserId && setValidCoach(false);
        return;
      }
    } else {
      if (!addTaskForm.patientId || (!addTaskForm.assignedUserId && !taskTypeOrder)) {
        !addTaskForm.patientId && setValidPatient(false);
        !addTaskForm.assignedUserId && setValidCoach(false);
        return;
      }
    }
    if (error.dueAt) {
      return;
    }
    setLoading(true);
    const addTaskMessage:string = "Task added successfully!";
    const addTaskStatus:boolean = true;
    try {
      if (isUpdate) {
        await taskApi.taskControllerUpdate(updateTaskForm, taskId as string);
        const isDone =
          statusList.find((state) => state.id === updateTaskForm.wfStateId)
            ?.status === Constants.DONE_STATUS;
        void logAppointmentActivity({
          patientId: selectedTask?.patientId as string,
          activityType: AppointmentActivityType.TASK,
          refId: taskId as string,
          action: isDone
            ? AppointmentActivityAction.COMPLETED
            : AppointmentActivityAction.UPDATED,
          title: selectedTask?.taskSubtype || selectedTask?.taskType,
        });
      } else {
        let newAddtaskForm=addTaskForm;
        if(appointmentId){
          newAddtaskForm  = { ...addTaskForm,
            sourceAppointmentId:appointmentId,
          };
        }
        newAddtaskForm={
          ...newAddtaskForm,
          productIds: orderItemsCheckbox,
          isSupplyOrder: taskTypeOrder
        }
        const response = await taskApi.taskControllerCreate(newAddtaskForm);
        // The API returns the saved task, id included, though SaveTask doesn't type it.
        const createdTaskId = (response.data as SaveTask & { id?: string }).id;
        if (createdTaskId) {
          void logAppointmentActivity({
            patientId: addTaskForm.patientId,
            activityType: AppointmentActivityType.TASK,
            refId: createdTaskId,
            // Supply orders are created already done.
            action: taskTypeOrder
              ? AppointmentActivityAction.COMPLETED
              : AppointmentActivityAction.STARTED,
            title: tasks.find((task) => task.id === addTaskForm.taskSubtypeId)
              ?.name,
          });
        }
        // if(!response.data.salesforceOrderCreated){
        //   addTaskMessage="Failed to create salesforce order" // error message
        // }
      }
      const toasterConfig = {
        type: addTaskStatus?"success":"danger",
        title: isUpdate ? "Task updated successfully!":addTaskMessage,
        icon: "check_circle_outline",
      };
      onSubmitTask(toasterConfig as ToastConfig);
      setTimeout(() => {
        handleDiscard();
      }, 1000);
      reloadGrid && reloadGrid();
    } catch (error) {
      const toasterConfig = {
        type: "danger",
        title: isUpdate ? "Error in updating task!!" : "Error in adding task!",
        icon: "error_outline",
      };
      onSubmitTask(toasterConfig as ToastConfig);
    } finally {
      setLoading(false);
    }
  };

  const getTaskType = async (id: string) => {
    if(id === undefined) {
      setTasks([]);
      setAddTaskForm({
        ...addTaskForm,
        taskSubtypeId: ''
      });
      return;
    }
    setLoading(true);
    try {
      const response = await taskApi.taskControllerFindAllTaskSubTypes(id, isUpdate? selectedTask?.patientId! : addTaskForm?.patientId!);
      setTasks(response.data);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const getTaskPriority = async () => {
    setLoading(true);
    try {
      const response = await lookup.lookupControllerFindAll(Lookups.TASK_PRIORITY);
      setPriority(response.data.sort((a: Lookup, b: Lookup) => a.sequence - b.sequence).map((value) => {
        return {id:value.id, type: value.name}
      }));
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const getTaskCategories = async () => {
    setLoading(true);
    try {
      const response = await taskApi.taskControllerFindAllTaskTypes();
      setCategories(response.data);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusList = () => {
    const list: TaskStatus[] = [];
    selectedTask?.state.nextStates.forEach((state: any) => {
      const status: TaskStatus = {id: state.stateId, status: state.stateName, stateOrder: state.stateOrder};
      list.push(status);
    });
    const status: TaskStatus = {id: selectedTask?.state.stateId, status: selectedTask?.state.stateName, stateOrder: selectedTask?.state.stateOrder};
    list.unshift(status);
    list.sort((a, b) => a.stateOrder - b.stateOrder);
    setStatusList(list);
  }

  useEffect(() => {
    getTaskCategories();
    getTaskPriority();
    getStatusList();
    getTaskType(selectedTask?.taskTypeId as string);
    console.log(appointmentId , "from charting appt id")

    if(appointmentId){
      setAddTaskForm({
        ...addTaskForm,
        sourceAppointmentId:appointmentId,
      });
    }

    setAddTaskForm({
      ...addTaskForm,
      patientId: patient ? patient.patientId : '',
    });
  }, []);

  const loadCategories = async (): Promise<TaskCategory[]> => {
    const response = await taskApi.taskControllerFindAllTaskTypes();
    setTaskTypes([...response.data]);
    return response.data;
  };

  const handleCheckboxChange = (e:React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked){
      setOrderItemsCheckbox([...orderItemsCheckbox, e.target.name])
    } else{
      setOrderItemsCheckbox([...orderItemsCheckbox.filter(item=>item !== e.target.name)])
    }
  }

  const patientSelected = !addTaskForm.patientId
  return (
    <>
      {loading && <Spinner />}
      <Form
        className="d-flex flex-column h-100"
        noValidate
        validated={validated}
        onSubmit={submitHandler}
      >
        <div className="flex-fill">
        <div>
          <label
            htmlFor="patient"
            className="form-label fs-12 fw-semibold mb-1"
          >
            {isUpdate ? "Patient" : "Add Patient"}
          </label>
          { selectedTask ? <a className="float-end fs-12 text-secondary a-link a-underline" href="#" onClick={() => {openInNewTab(`/patients/${selectedTask?.patientId}/summary`);}}>View Profile</a> : '' }
          { !selectedTask && addTaskForm.patientId ? <a className="float-end fs-12 text-secondary a-link a-underline" href="#" onClick={() => {
            openInNewTab(`/patients/${addTaskForm.patientId}/summary`);
          }}>View Profile</a> : '' }
        </div>
        <AsyncTypeahead
                  name="patientId"
                  options={patientsList}
                  onSelect={selectPatient}
                  callback={loadPatients}
                  placeholder={"Search Patient"}
                  defaultValue={patient ? patient.firstName + ' ' + patient.lastName : selectedTask?.patientName}
                  required={true}
                  isValid={validPatient}
                  unsetId={unsetPatientId}
                  disabled={isUpdate || Boolean(patient)}
                ></AsyncTypeahead>
          <div className="row g-2 mt-2">
            <div className="col-6 mb-3">
              <label
                htmlFor="categoryId"
                className="form-label fs-12 fw-semibold mb-1"
              >
                Task Type
              </label>
              {/* <select
                className="form-select form-select-sm "
                name="taskTypeId"
                value={addTaskForm.taskTypeId}
                onChange={(e) => { formChangeHandlar(e) }}
                required disabled={isUpdate}
              >
                <option value="" disabled >Select Task Type</option>
                {categories.map((category: TaskCategory, index: number) => (
                  <option key={index} value={category.taskTypeId}>
                    {category.taskType}
                  </option>
                ))}
              </select> */}
              <CommonDropdown
                label="Task Type"
                keyProvider={item => item.id}
                labelProvider={item => item.name}
                initialValue={selectedTask?.taskTypeId || addTaskForm.taskTypeId}
                dataProvider={loadCategories}
                onChange={(category) => onCategoryUpdate(category?.id!)}
                required={true}
                disabled={isUpdate?true:patientSelected}></CommonDropdown>
            </div>
            {/* Date field */}
            <div className="col-6 mb-3">
              <label
                htmlFor="dueAt"
                className="form-label fs-12 fw-semibold mb-1"
              >
                Due Date
              </label>
              <div className="mobiscroll">
                <div className="mobiscroll__calendar">
                  <Datepicker
                    id="dueAt"
                    controls={["calendar"]}
                    calendarType="month"
                    theme="windows"
                    themeVariant="light"
                    placeholder="MM/DD/YYYY"
                    display="bubble"
                    touchUi={true}
                    buttons={[]}
                    cssClass={`${error.dueAt ? 'invalid-input' : ''}`}
                    value={selectedTask ? formatDateTask(updateTaskForm.dueAt!) : formatDateTask(addTaskForm.dueAt)}
                    // showOnFocus={false}
                    showOnClick={false}
                    isOpen={openPicker}
                    onClose={() => {setOpenPicker(false)}}
                    min={ selectedTask?.dueAt && new Date(selectedTask.dueAt) < new Date()
                      ? formatDateTask(selectedTask.dueAt)
                      : new Date() }
                    onChange={(e) => dateRangeChangeHandlar('dueAt', e)}
                    disabled={selectedTask?.state.isEnd ?true: isUpdate? false:patientSelected}
                    required
                  />
                </div>
                  <div onClick={() => {if(!patientSelected || enableDueDate)setOpenPicker(!openPicker)}} className="mobiscroll__calendar-icon">
                      <span className="material-symbols-outlined fs-18" >
                        calendar_month
                      </span>
                  </div>
              </div>
            </div>
            <div className="col-6 mb-3">
              <label htmlFor="task" className="form-label fs-12 fw-semibold mb-1">
                Task
              </label>
              <select
                id="task"
                className="form-select form-select-sm"
                name="taskSubtypeId"
                value={isUpdate ? selectedTask?.taskSubtypeId : addTaskForm.taskSubtypeId}
                onChange={formChangeHandlar}
                required
                disabled={isUpdate?true: patientSelected}
              >
                <option value="">Select Task</option>
                {tasks.map((type: TaskType, index) => (
                  <option key={index} value={type.id}>
                    {type.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-6 mb-3">
              <label
                htmlFor="priority"
                className="form-label fs-12 fw-semibold mb-1"
              >
                Priority
              </label>
              {/* <select
                id="priority"
                className="form-select form-select-sm"
                name="priorityId"
                value={isUpdate ? updateTaskForm.priorityId : addTaskForm.priorityId}
                onChange={formChangeHandlar}
                required
                disabled={selectedTask?.state.isEnd}
              >
                <option value="" >Select Priority</option>
                {priority.map((priority: TaskPriority, index) => (
                  <option key={index} value={priority.id}>
                    {priority.type}
                  </option>
                ))}
              </select> */}


              <LookupDropdown
                label="Priority"
                initialValue={isUpdate ? updateTaskForm.priorityId : addTaskForm.priorityId}
                type={Lookups.TASK_PRIORITY}
                onChange={(priority) => onPriorityUpdate(priority?.id!)}
                disabled={selectedTask?.state.isEnd?true:isUpdate? false: patientSelected}
                required={true}
                changeSelectionKey={forceProirityChange}
              />
            </div>
            {taskTypeOrder&&
            <div className="col-12 mb-3 row g-2">
              <span
                className="form-label fs-12 fw-semibold mb-1"
                >
                {"Order Items"}
              </span>
              {orderItems.map((orderItem,idx)=>{
                return(
                  <div className={`col-${idx%2?"5":"7"}`}>
                  <input
                    className="form-check-input m-1"
                    type="checkbox"
                    name={orderItem.id}
                    checked={orderItemsCheckbox.includes(orderItem.id)}
                    onChange={(e) =>
                      handleCheckboxChange(e)
                      }
                      disabled={orderItem.isRequired}
                      />
                  <span
                    className="fs-15 mb-1"
                    >
                    {orderItem.productName}
                  </span>
                  </div>
                )
              })}
            </div>}

            <div className={isUpdate ? 'col-7 mb-3' : 'col-12 mb-3'}>
              <label
                htmlFor="assignTo"
                className="form-label fs-12 fw-semibold mb-1"
              >
                Assignee
              </label>
              <AsyncTypeahead
                name="assignedUserId"
                options={coachList}
                onSelect={selectCoach}
                callback={loadCoachesList}
                placeholder={"Search Coach / Wellinks User"}
                defaultValue={selectedTask?.assignedUser}
                required={requireAssignee}
                isValid={validCoach}
                disabled={selectedTask?.state.isEnd?true:isUpdate? false: patientSelected}
                addClass={'form-control-sm'}
              ></AsyncTypeahead>
            </div>
            {
              isUpdate && <div className="col-5 mb-3">
              <label
                htmlFor="taskStatus"
                className="form-label fs-12 fw-semibold mb-1"
              >
                Status
              </label>
              <select
                className="form-select form-select-sm "
                name="taskStatus"
                value={updateTaskForm.taskStatus}
                onChange={(e) => { updateFormChangeHandlar(e) }}
                disabled={selectedTask?.state.isEnd}
                required
              >
                <option value="" >Select Status</option>
                {statusList.map((state: TaskStatus, index) => (
                  <option key={index} value={state.id}>
                    {state.status}
                  </option>
                ))}
              </select>
            </div>
            }
            <div className="col-12 mb-3">
              <label htmlFor="Notes" className="form-label">
                Comments
              </label>
              <textarea
                name="comment"
                value={isUpdate ? updateTaskForm.comment : addTaskForm.comment}
                onChange={formChangeHandlar}
                className="form-control form-control-sm"
                id="Notes"
                placeholder="Add relevant Comments"
                rows={6}
                disabled={selectedTask?.state.isEnd ?true : isUpdate? false: patientSelected}
              ></textarea>
            </div>
          </div>
        </div>
        <div className="offcanvas-footer w-100 border-top d-flex justify-content-between border-top ">
          <div>
            {!selectedTask?.state.isEnd && <button
              className="btn btn-outline-secondary rounded-pill px-5 fs-13"
              type="reset"
              onClick={handleDiscard}
            >
              Discard
            </button>}
          </div>
          <div>

          {selectedTask?.state.isEnd && <button
            className="btn btn-outline-secondary rounded-pill px-5 fs-13"
            type="reset"
            onClick={handleDiscard}
          >
            Close
          </button>}

          {!selectedTask?.state.isEnd && <button
              className="btn btn-secondary rounded-pill px-5 fs-13"
              type="submit"
            >
              { isUpdate ? 'Update' : (
                <>
                  {/* <span className="material-symbols-outlined fs-16 align-text-top">
                    add
                  </span> */}
                  Add
                </>)}
            </button>}
          </div>
        </div>
      </Form>
    </>
  );
};
export default AddUpdateTask;
